import FileOfflineButton from "../components/FileOfflineButton";
import { useEffect, useMemo, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Folder, ChevronRight, ChevronDown, Plus, Pencil, Trash2,
  FileText, Music2, Video, Link2, Heart, Upload, Search, ExternalLink, Download, Maximize2, X,
  LayoutGrid, List, PanelLeftClose, PanelLeftOpen,
} from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import MediaPlayer from "@/components/MediaPlayer";
import ConfirmDialog from "@/components/ConfirmDialog";
import { toast } from "sonner";

const FILE_ICONS = { partition: FileText, audio: Music2, video: Video, link: Link2 };
const KIND_STYLE = { partition: "text-rose-400", audio: "text-violet-400", video: "text-sky-400", link: "text-emerald-400" };

export default function DocumentsPage() {
  const { user, t } = useAuth();
  const canWrite = ["admin", "music_leader"].includes(user?.role);
  const [params, setParams] = useSearchParams();
  const [folders, setFolders] = useState([]);
  const [files, setFiles] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [selected, setSelected] = useState(params.get("folder") || null);
  const [player, setPlayer] = useState(null);
  const [pdf, setPdf] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [renameOpen, setRenameOpen] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkName, setLinkName] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  const [search, setSearch] = useState("");
  const [searchRes, setSearchRes] = useState(null);
  const [favorites, setFavorites] = useState({});
  const [dragOver, setDragOver] = useState(false);
  const [loadingTree, setLoadingTree] = useState(true);
  const [actsFor, setActsFor] = useState(null);
  const [viewMode, setViewMode] = useState(() => localStorage.getItem("rannea_docs_view") || "grid");
  const [treeOpen, setTreeOpen] = useState(true);
  const changeView = (m) => { setViewMode(m); localStorage.setItem("rannea_docs_view", m); };

  const loadTree = useCallback(() => {
    setLoadingTree(true);
    api.get("/documents/tree")
      .then((r) => setFolders(r.data))
      .catch((e) => toast.error(e.friendly))
      .finally(() => setLoadingTree(false));
  }, []);
  const loadFavs = useCallback(() => {
    api.get("/favorites").then((r) => {
      const map = {};
      r.data.forEach((f) => { map[f.target_id] = f.id; });
      setFavorites(map);
    }).catch(() => {});
  }, []);
  const loadFiles = useCallback((folderId) => {
    if (!folderId) { setFiles([]); return; }
    api.get(`/files?folder_id=${folderId}`).then((r) => setFiles(r.data)).catch(() => setFiles([]));
  }, []);

  useEffect(() => { loadTree(); loadFavs(); }, [loadTree, loadFavs]);
  useEffect(() => { loadFiles(selected); }, [selected, loadFiles]);

  const openFile = useCallback((f) => {
    api.post(`/files/${f.id}/open`).catch(() => {});
    if (f.kind === "link") window.open(f.url, "_blank");
    else if (f.kind === "partition") setPdf(f);
    else setPlayer(f);
  }, []);

  useEffect(() => {
    const openId = params.get("open");
    if (openId && files.length > 0) {
      const f = files.find((x) => x.id === openId);
      if (f) {
        openFile(f);
        params.delete("open");
        setParams(params, { replace: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files]);

  const childrenOf = useMemo(() => {
    const map = {};
    folders.forEach((f) => {
      const p = f.parent_id || "root";
      map[p] = map[p] || [];
      map[p].push(f);
    });
    return map;
  }, [folders]);
  const folderMap = useMemo(() => Object.fromEntries(folders.map((f) => [f.id, f])), [folders]);
  const subfolders = childrenOf[selected || "root"] || [];

  const visibleFolders = useMemo(() => {
    const list = [];
    const walk = (parentKey, depth) => {
      (childrenOf[parentKey] || []).forEach((f) => {
        list.push({ folder: f, depth });
        if (expanded[f.id]) walk(f.id, depth + 1);
      });
    };
    walk("root", 0);
    return list;
  }, [childrenOf, expanded]);

  const breadcrumb = useMemo(() => {
    const path = [];
    let cur = selected ? folderMap[selected] : null;
    while (cur) {
      path.unshift(cur);
      cur = cur.parent_id ? folderMap[cur.parent_id] : null;
    }
    return path;
  }, [selected, folderMap]);

  const selectFolder = (id) => {
    setSelected(id);
    setExpanded((e) => ({ ...e, [id]: true }));
  };

  const addFolder = async () => {
    if (!newName.trim()) return;
    try {
      await api.post("/documents/folders", { parent_id: selected, name: newName.trim() });
      toast.success(t("docs.folder_created"));
      setNewName(""); setAddOpen(false);
      loadTree();
    } catch (e) { toast.error(e.friendly); }
  };

  const rename = async () => {
    try {
      await api.put(`/documents/folders/${selected}`, { name: newName.trim() });
      toast.success(t("docs.renamed"));
      setRenameOpen(false);
      loadTree();
    } catch (e) { toast.error(e.friendly); }
  };

  const remove = async () => {
    try {
      await api.delete(`/documents/folders/${selected}`);
      toast.success(t("docs.deleted"));
      setConfirmDel(false); setSelected(null);
      loadTree();
    } catch (e) { toast.error(e.friendly); }
  };

  const [renameFile, setRenameFile] = useState(null);
  const [renameFileVal, setRenameFileVal] = useState("");
  const [pendingUpload, setPendingUpload] = useState(null);
  const [uploadProg, setUploadProg] = useState(null);

  const submitRenameFile = async () => {
    if (!renameFileVal.trim()) return;
    try {
      await api.put(`/files/${renameFile.id}`, { name: renameFileVal.trim() });
      toast.success(t("docs.file_renamed"));
      setRenameFile(null);
      loadFiles(selected);
    } catch (e) { toast.error(e.friendly); }
  };

  const upload = async (fileList) => {
    const items = Array.from(fileList);
    const total = items.length;
    let i = 0;
    for (const item of items) {
      i++;
      const f = item.file || item;
      const name = (item.name || f.name).trim() || f.name;
      const toSend = name === f.name ? f : new File([f], name, { type: f.type });
      const fd = new FormData();
      fd.append("file", toSend);
      fd.append("context", "documents");
      fd.append("kind", "auto");
      fd.append("folder_id", selected);
      try {
        await api.post("/files/upload", fd, {
          onUploadProgress: (e) => setUploadProg({ name, pct: Math.round((e.loaded * 100) / (e.total || f.size || 1)), index: i, total }),
        });
      } catch (e) { toast.error(`${name} : ${e.friendly}`); }
    }
    setUploadProg(null);
    toast.success(t("docs.import_done"));
    loadFiles(selected);
  };

  const addLink = async () => {
    try {
      await api.post("/files/link", { context: "documents", folder_id: selected, name: linkName, url: linkUrl });
      toast.success(t("docs.link_added"));
      setLinkOpen(false); setLinkName(""); setLinkUrl("");
      loadFiles(selected);
    } catch (e) { toast.error(e.friendly); }
  };

  const deleteFile = async (f) => {
    await api.delete(`/files/${f.id}`).catch((e) => toast.error(e.friendly));
    loadFiles(selected);
  };

  const toggleFav = async (f) => {
    const favId = favorites[f.id];
    try {
      if (favId) await api.delete(`/favorites/${favId}`);
      else await api.post("/favorites", { target_type: "document", target_id: f.id, name: f.name, path: `/documents?folder=${f.folder_id}&open=${f.id}` });
      loadFavs();
    } catch (e) { toast.error(e.friendly); }
  };

  const doSearch = async (q) => {
    setSearch(q);
    if (q.trim().length < 2) { setSearchRes(null); return; }
    try {
      const { data } = await api.get(`/search?q=${encodeURIComponent(q)}`);
      setSearchRes(data);
    } catch {}
  };

  const toggleExpand = (id, v) => setExpanded((x) => ({ ...x, [id]: v }));

  // Ligne d'actions d'un fichier, commune aux vues grille et liste
  const FileActions = ({ f }) => (
    <div className="flex items-center justify-end gap-1 flex-wrap" data-testid={`doc-file-actions-${f.id}`}>
      {f.kind !== "link" && (
        <a href={fileUrl(f.id, "download=1")} download={f.name} className="p-1.5 t-secondary hover:text-white" title={t("docs.download")} aria-label={t("docs.download")} data-testid={`doc-file-download-${f.id}`}>
          <Download className="h-4 w-4" />
        </a>
      )}
      {f.kind !== "link" && <FileOfflineButton fileId={f.id} />}
      <button onClick={() => toggleFav(f)} className="p-1.5" data-testid={`fav-doc-${f.id}`} aria-label={t("nav.favorites")} title={t("nav.favorites")}>
        <Heart className={`h-4 w-4 ${favorites[f.id] ? "fill-rose-400 text-rose-400" : "t-secondary hover:text-rose-400"}`} />
      </button>
      {canWrite && f.kind !== "link" && (
        <button onClick={() => { setRenameFile(f); setRenameFileVal(f.name); }} className="p-1.5 t-secondary hover:text-white transition-colors" data-testid={`doc-file-rename-${f.id}`} title={t("common.rename")} aria-label={t("common.rename")}>
          <Pencil className="h-4 w-4" />
        </button>
      )}
      {canWrite && (
        <button onClick={() => deleteFile(f)} className="p-1.5 t-secondary hover:text-rose-400 transition-colors" data-testid={`doc-file-delete-${f.id}`} title={t("common.delete")} aria-label={t("common.delete")}>
          <Trash2 className="h-4 w-4" />
        </button>
      )}
    </div>
  );

  const FolderTile = ({ folder }) => (
    <button onClick={() => selectFolder(folder.id)} className="app-card p-4 flex flex-col items-center gap-2 text-center hover:ring-2 hover:ring-emerald-400/50 transition-shadow group" data-testid={`doc-folder-tile-${folder.id}`}>
      <Folder className="h-10 w-10 text-amber-400 group-hover:scale-105 transition-transform" />
      <span className="text-xs md:text-sm font-medium leading-tight break-words w-full line-clamp-2">{folder.name}</span>
    </button>
  );

  const FolderRow = ({ folder }) => (
    <div className="flex items-center gap-2 md:gap-3 px-2.5 md:px-3 py-2.5 rounded-lg hover:bg-white/5 cursor-pointer" onClick={() => selectFolder(folder.id)} data-testid={`doc-folder-tile-${folder.id}`}>
      <Folder className="h-4 w-4 shrink-0 text-amber-400" />
      <span className="flex-1 min-w-0 text-xs md:text-sm truncate font-medium">{folder.name}</span>
      <ChevronRight className="h-4 w-4 t-secondary shrink-0" />
    </div>
  );

  const FileTile = ({ f }) => {
    const Icon = FILE_ICONS[f.kind] || FileText;
    return (
      <div className="app-card p-4 flex flex-col items-center gap-2 text-center hover:ring-2 hover:ring-emerald-400/50 transition-shadow" data-testid={`doc-file-row-${f.id}`}>
        <button onClick={() => openFile(f)} className="flex flex-col items-center gap-2 w-full min-w-0" data-testid={`doc-file-open-${f.id}`}>
          <Icon className={`h-10 w-10 ${KIND_STYLE[f.kind] || "brand"}`} />
          <span className="text-xs md:text-sm font-medium leading-tight break-words w-full line-clamp-2">{f.name}</span>
        </button>
        <div className="flex items-center gap-1">
          {f.kind === "link" && <ExternalLink className="h-3.5 w-3.5 t-secondary" />}
          <button onClick={() => setActsFor(actsFor === f.id ? null : f.id)} className="p-1 t-secondary hover:text-white transition-colors" data-testid={`doc-file-actions-toggle-${f.id}`} aria-expanded={actsFor === f.id} aria-label={t("usr.col_actions")} title={t("usr.col_actions")}>
            <ChevronDown className={`h-4 w-4 transition-transform ${actsFor === f.id ? "rotate-180" : ""}`} />
          </button>
        </div>
        {actsFor === f.id && <FileActions f={f} />}
      </div>
    );
  };

  const FileRow = ({ f }) => {
    const Icon = FILE_ICONS[f.kind] || FileText;
    return (
      <div className="rounded-lg hover:bg-white/5 group" data-testid={`doc-file-row-${f.id}`}>
        <div className="flex items-center gap-2 md:gap-3 px-2.5 md:px-3 py-2.5">
          <Icon className={`h-4 w-4 shrink-0 ${KIND_STYLE[f.kind] || "brand"}`} />
          <button className="flex-1 min-w-0 text-left text-xs md:text-sm truncate hover:underline" onClick={() => openFile(f)} data-testid={`doc-file-open-${f.id}`}>
            {f.name}
          </button>
          {f.kind === "link" && <ExternalLink className="h-3.5 w-3.5 t-secondary shrink-0" />}
          <button onClick={() => setActsFor(actsFor === f.id ? null : f.id)} className="p-1 -mr-1 t-secondary hover:text-white transition-colors shrink-0" data-testid={`doc-file-actions-toggle-${f.id}`} aria-expanded={actsFor === f.id} aria-label={t("usr.col_actions")} title={t("usr.col_actions")}>
            <ChevronDown className={`h-4 w-4 transition-transform ${actsFor === f.id ? "rotate-180" : ""}`} />
          </button>
        </div>
        {actsFor === f.id && <div className="px-2.5 md:px-3 pb-2"><FileActions f={f} /></div>}
      </div>
    );
  };

  const GRID_CLS = "grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3";

  return (
    <div className="flex flex-col lg:flex-row gap-4 flex-1" data-testid="documents-page">
      <div className={`lg:w-72 shrink-0 app-card p-3 ${treeOpen ? "" : "hidden"}`} data-testid="documents-tree-panel">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs t-secondary uppercase tracking-wider px-1">{t("docs.folders_section")}</span>
          <button onClick={() => setTreeOpen(false)} className="p-1.5 rounded-lg t-secondary hover:bg-white/5" data-testid="documents-tree-close" title={t("docs.tree_hide")} aria-label={t("docs.tree_hide")}><PanelLeftClose className="h-4 w-4" /></button>
        </div>
        <div className="relative mb-3">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 t-secondary" />
          <Input value={search} onChange={(e) => doSearch(e.target.value)} placeholder={t("docs.search_placeholder")} className="pl-9 bg-transparent border-app h-9 text-sm" data-testid="documents-search-input" />
        </div>
        {searchRes ? (
          <div className="space-y-1" data-testid="documents-search-results">
            {(searchRes.folders || []).map((f) => (
              <button key={f.id} onClick={() => { selectFolder(f.id); setSearch(""); setSearchRes(null); }} className="w-full text-left px-2 py-1.5 rounded hover:bg-white/5 text-sm flex items-center gap-2" data-testid={`search-folder-${f.id}`}>
                <Folder className="h-3.5 w-3.5 accent" /> {f.name}
              </button>
            ))}
            {(searchRes.files || []).filter((f) => f.context === "documents").map((f) => (
              <button key={f.id} onClick={() => { selectFolder(f.folder_id); setSearch(""); setSearchRes(null); }} className="w-full text-left px-2 py-1.5 rounded hover:bg-white/5 text-sm flex items-center gap-2" data-testid={`search-file-${f.id}`}>
                <FileText className="h-3.5 w-3.5 brand" /> {f.name}
              </button>
            ))}
            {!(searchRes.folders?.length || (searchRes.files || []).filter((f) => f.context === "documents").length) && <p className="text-xs t-secondary px-2">{t("docs.no_results")}</p>}
          </div>
        ) : (
          <div className="space-y-0.5 max-h-[60vh] overflow-y-auto" data-testid="documents-tree">
            {loadingTree && <p className="text-xs t-secondary px-2 py-4" data-testid="documents-loading">{t("docs.loading")}</p>}
            {visibleFolders.map(({ folder, depth }) => {
              const hasKids = (childrenOf[folder.id] || []).length > 0;
              const isOpen = expanded[folder.id];
              return (
                <div
                  key={folder.id}
                  className={`flex items-center gap-1 py-1.5 px-2 rounded-lg cursor-pointer text-sm transition-colors ${selected === folder.id ? "brand-bg text-white" : "hover:bg-white/5"}`}
                  style={{ paddingLeft: `${depth * 14 + 8}px` }}
                  onClick={() => selectFolder(folder.id)}
                  data-testid={`doc-folder-${folder.id}`}
                >
                  {hasKids ? (
                    <button onClick={(e) => { e.stopPropagation(); toggleExpand(folder.id, !isOpen); }} className="shrink-0" data-testid={`doc-toggle-${folder.id}`}>
                      {isOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                    </button>
                  ) : <span className="w-3.5" />}
                  <Folder className="h-4 w-4 shrink-0 accent" />
                  <span className="truncate">{folder.name}</span>
                </div>
              );
            })}
            {folders.length === 0 && <p className="text-xs t-secondary px-2 py-4">{t("docs.no_folder")}{canWrite ? t("docs.no_folder_create") : ""}</p>}
          </div>
        )}
        {canWrite && (
          <Button size="sm" variant="outline" className="w-full mt-3 border-app" onClick={() => { setSelected(null); setNewName(""); setAddOpen(true); }} data-testid="add-folder-button">
            <Plus className="h-4 w-4 mr-1" /> {t("docs.new_folder")}
          </Button>
        )}
      </div>

      <div
        className={`flex-1 app-card p-5 min-h-[50vh] transition-shadow ${dragOver ? "ring-2 ring-emerald-400" : ""}`}
        data-testid="documents-content"
        onDragOver={(e) => {
          if (canWrite && selected) { e.preventDefault(); setDragOver(true); }
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          if (canWrite && selected && e.dataTransfer.files?.length) upload(e.dataTransfer.files);
        }}
      >
        <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
          <div className="flex items-center gap-1 text-xs t-secondary flex-wrap min-w-0" data-testid="documents-breadcrumb">
            {!treeOpen && (
              <button onClick={() => setTreeOpen(true)} className="p-1.5 rounded-lg border border-app hover:bg-white/5 mr-1 shrink-0" data-testid="documents-tree-open" title={t("docs.tree_show")} aria-label={t("docs.tree_show")}><PanelLeftOpen className="h-4 w-4" /></button>
            )}
            <button className={`hover:underline ${!selected ? "text-white font-semibold" : ""}`} onClick={() => selectFolder(null)} data-testid="breadcrumb-root">{t("nav.documents")}</button>
            {breadcrumb.map((b) => (
              <span key={b.id} className="flex items-center gap-1">
                <ChevronRight className="h-3 w-3" />
                <button className={`hover:underline ${selected === b.id ? "text-white font-semibold" : ""}`} onClick={() => selectFolder(b.id)}>{b.name}</button>
              </span>
            ))}
          </div>
          <div className="flex items-center gap-1 border border-app rounded-lg p-0.5 shrink-0" data-testid="documents-view-toggle">
            <button onClick={() => changeView("grid")} className={`p-1.5 rounded-md transition-colors ${viewMode === "grid" ? "brand-bg text-white" : "t-secondary hover:bg-white/5"}`} data-testid="view-grid-button" title={t("docs.view_grid")} aria-label={t("docs.view_grid")}><LayoutGrid className="h-4 w-4" /></button>
            <button onClick={() => changeView("list")} className={`p-1.5 rounded-md transition-colors ${viewMode === "list" ? "brand-bg text-white" : "t-secondary hover:bg-white/5"}`} data-testid="view-list-button" title={t("docs.view_list")} aria-label={t("docs.view_list")}><List className="h-4 w-4" /></button>
          </div>
        </div>
        {!selected ? (
          subfolders.length === 0 ? (
            <p className="t-secondary text-sm py-10 text-center">{t("docs.select_folder")}</p>
          ) : viewMode === "grid" ? (
            <div className={GRID_CLS} data-testid="doc-root-grid">
              {subfolders.map((f) => <FolderTile key={f.id} folder={f} />)}
            </div>
          ) : (
            <div className="space-y-1" data-testid="doc-root-list">
              {subfolders.map((f) => <FolderRow key={f.id} folder={f} />)}
            </div>
          )
        ) : (
          <>
            <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
              <h2 className="font-heading text-xl font-bold" data-testid="selected-folder-name">{folderMap[selected]?.name}</h2>
              {canWrite && (
                <div className="flex gap-2 flex-wrap">
                  <Button size="sm" className="brand-bg text-white" onClick={() => { setNewName(""); setAddOpen(true); }} data-testid="add-subfolder-button">
                    <Plus className="h-4 w-4 mr-1" /> {t("docs.subfolder")}
                  </Button>
                  <Button size="sm" variant="outline" className="border-app" onClick={() => { setNewName(folderMap[selected]?.name || ""); setRenameOpen(true); }} data-testid="rename-folder-button">
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button size="sm" variant="outline" className="border-app text-rose-400" onClick={() => setConfirmDel(true)} data-testid="delete-folder-button">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>
            {canWrite && (
              <div className="flex gap-2 mb-4 flex-wrap">
                <label className="cursor-pointer">
                  <input type="file" multiple accept=".jpg,.jpeg,.png,.svg,.eps,.pdf,.mp4,.mov,.webm,.mp3,.wav" className="hidden" onChange={(e) => { if (e.target.files?.length) setPendingUpload(Array.from(e.target.files).map((f) => ({ file: f, name: f.name }))); e.target.value = ""; }} data-testid="doc-upload-input" />
                  <span className="inline-flex items-center gap-2 px-4 py-2 rounded-lg brand-bg text-white text-sm font-medium">
                    <Upload className="h-4 w-4" /> {t("docs.add_files")}
                  </span>
                </label>
                <Button size="sm" variant="outline" className="border-app h-9" onClick={() => setLinkOpen(true)} data-testid="doc-add-link-button">
                  <Link2 className="h-4 w-4 mr-1" /> {t("docs.add_link")}
                </Button>
              </div>
            )}
            {subfolders.length > 0 && (
              <>
                <p className="text-xs t-secondary uppercase tracking-wider mb-2">{t("docs.folders_section")}</p>
                {viewMode === "grid" ? (
                  <div className={`${GRID_CLS} mb-5`} data-testid="doc-subfolders-grid">
                    {subfolders.map((f) => <FolderTile key={f.id} folder={f} />)}
                  </div>
                ) : (
                  <div className="space-y-1 mb-5" data-testid="doc-subfolders-list">
                    {subfolders.map((f) => <FolderRow key={f.id} folder={f} />)}
                  </div>
                )}
              </>
            )}
            <p className="text-xs t-secondary uppercase tracking-wider mb-2">{t("docs.files_section")}</p>
            {files.length === 0 && <p className="text-sm t-secondary py-6 text-center">{t("docs.no_file")}{canWrite && t("docs.no_file_hint")}</p>}
            {viewMode === "grid" ? (
              <div className={GRID_CLS} data-testid="doc-files-list">
                {files.map((f) => <FileTile key={f.id} f={f} />)}
              </div>
            ) : (
              <div className="space-y-1" data-testid="doc-files-list">
                {files.map((f) => <FileRow key={f.id} f={f} />)}
              </div>
            )}
          </>
        )}
      </div>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="card-bg border-app" data-testid="add-folder-dialog">
          <DialogHeader><DialogTitle className="font-heading">{t(selected ? "docs.new_subfolder" : "docs.new_folder")}</DialogTitle><DialogDescription className="sr-only">{t("docs.create_folder_sr")}</DialogDescription></DialogHeader>
          <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder={t("usr.col_name")} className="bg-transparent border-app" data-testid="new-folder-name-input" autoFocus />
          <Button className="brand-bg text-white" onClick={addFolder} disabled={!newName.trim()} data-testid="add-folder-confirm">{t("common.create")}</Button>
        </DialogContent>
      </Dialog>

      <Dialog open={renameOpen} onOpenChange={setRenameOpen}>
        <DialogContent className="card-bg border-app" data-testid="rename-folder-dialog">
          <DialogHeader><DialogTitle className="font-heading">{t("common.rename")}</DialogTitle><DialogDescription className="sr-only">{t("docs.rename_folder_sr")}</DialogDescription></DialogHeader>
          <Input value={newName} onChange={(e) => setNewName(e.target.value)} className="bg-transparent border-app" data-testid="rename-folder-input" autoFocus />
          <Button className="brand-bg text-white" onClick={rename} disabled={!newName.trim()} data-testid="rename-folder-confirm">{t("common.save")}</Button>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={confirmDel}
        title={t("usr.delete_title")}
        description={t("docs.delete_folder_desc")}
        onConfirm={remove}
        onClose={() => setConfirmDel(false)}
        testid="delete-folder-confirm-dialog"
      />

      <Dialog open={linkOpen} onOpenChange={setLinkOpen}>
        <DialogContent className="card-bg border-app" data-testid="doc-add-link-dialog">
          <DialogHeader><DialogTitle className="font-heading">{t("docs.add_link")}</DialogTitle><DialogDescription className="sr-only">{t("docs.add_link_sr")}</DialogDescription></DialogHeader>
          <Input value={linkName} onChange={(e) => setLinkName(e.target.value)} placeholder={t("docs.link_name")} className="bg-transparent border-app" data-testid="doc-link-name-input" />
          <Input value={linkUrl} onChange={(e) => setLinkUrl(e.target.value)} placeholder="https://…" className="bg-transparent border-app" data-testid="doc-link-url-input" />
          <Button className="brand-bg text-white" onClick={addLink} disabled={!linkName.trim() || !linkUrl.trim()} data-testid="doc-add-link-confirm">{t("common.add")}</Button>
        </DialogContent>
      </Dialog>

      <Dialog open={!!pendingUpload} onOpenChange={(v) => !v && setPendingUpload(null)}>
        <DialogContent className="card-bg border-app" data-testid="doc-upload-names-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{t("docs.upload_names_title")}</DialogTitle>
            <DialogDescription className="sr-only">{t("docs.upload_names_sr")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-72 overflow-y-auto">
            {(pendingUpload || []).map((it, i) => (
              <Input key={i} value={it.name} onChange={(e) => setPendingUpload((p) => p.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} className="bg-transparent border-app" data-testid={`doc-upload-name-input-${i}`} />
            ))}
          </div>
          <Button className="w-full brand-bg text-white" onClick={async () => { const items = pendingUpload; setPendingUpload(null); await upload(items); }} data-testid="doc-upload-names-confirm">{t("docs.import")}</Button>
        </DialogContent>
      </Dialog>

      {uploadProg && (
        <div className="fixed bottom-20 md:bottom-6 right-4 left-4 md:left-auto md:w-80 app-card p-4 z-50 anim-fade-up" data-testid="upload-progress-card">
          <div className="flex items-center justify-between text-xs mb-2 gap-2">
            <span className="truncate font-medium">{uploadProg.name}</span>
            <span className="accent font-bold shrink-0" data-testid="upload-progress-pct">{uploadProg.pct}%</span>
          </div>
          <div className="h-2 rounded-full bg-white/10 overflow-hidden">
            <div className="h-full accent-bg transition-all duration-200" style={{ width: `${uploadProg.pct}%` }} data-testid="upload-progress-bar" />
          </div>
          {uploadProg.total > 1 && <p className="text-[10px] t-secondary mt-1.5">{t("docs.file_counter", { i: uploadProg.index, total: uploadProg.total })}</p>}
        </div>
      )}

      <Dialog open={!!renameFile} onOpenChange={(v) => !v && setRenameFile(null)}>
        <DialogContent className="card-bg border-app" data-testid="doc-rename-file-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{t("docs.rename_file_title")}</DialogTitle>
            <DialogDescription className="sr-only">{t("docs.rename_file_title")}</DialogDescription>
          </DialogHeader>
          <Input value={renameFileVal} onChange={(e) => setRenameFileVal(e.target.value)} className="bg-transparent border-app" data-testid="doc-rename-file-input" autoFocus onKeyDown={(e) => e.key === "Enter" && submitRenameFile()} />
          <Button className="w-full brand-bg text-white" onClick={submitRenameFile} data-testid="doc-rename-file-save">{t("common.rename")}</Button>
        </DialogContent>
      </Dialog>

      {pdf && (
        <Dialog open={true} onOpenChange={() => setPdf(null)}>
          <DialogContent hideClose className="card-bg border-app max-w-5xl h-[90vh] flex flex-col fullscreen:h-full fullscreen:max-w-none fullscreen:w-screen" data-testid="doc-pdf-viewer-dialog">
            <DialogHeader className="flex-row items-center justify-between gap-2 space-y-0">
              <DialogTitle className="font-heading truncate">{pdf.name}</DialogTitle>
              <div className="flex items-center gap-1 shrink-0">
                <button
                  onClick={() => {
                    if (document.fullscreenElement) { document.exitFullscreen(); return; }
                    const el = document.querySelector('[data-testid="doc-pdf-viewer-dialog"]');
                    if (el && el.requestFullscreen) el.requestFullscreen().catch(() => window.open(fileUrl(pdf.id), "_blank"));
                    else window.open(fileUrl(pdf.id), "_blank");
                  }}
                  className="t-secondary hover:text-white p-2"
                  data-testid="doc-pdf-fullscreen-button"
                  title={t("docs.fullscreen")}
                >
                  <Maximize2 className="h-4 w-4" />
                </button>
                <button
                  onClick={() => { if (document.fullscreenElement) document.exitFullscreen().catch(() => {}); setPdf(null); }}
                  className="t-secondary hover:text-white p-2"
                  data-testid="doc-pdf-close-button"
                  title={t("common.close")}
                  aria-label={t("common.close")}
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <DialogDescription className="sr-only">{t("docs.pdf_reader_sr")}</DialogDescription>
            </DialogHeader>
            <iframe src={fileUrl(pdf.id)} title={pdf.name} className="flex-1 w-full rounded-lg bg-white" data-testid="doc-pdf-viewer-frame" />
          </DialogContent>
        </Dialog>
      )}

      {player && <MediaPlayer file={player} onClose={() => setPlayer(null)} />}
    </div>
  );
}
